import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, Loader2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { key: 'og_access_plan_id',     label: 'OG Access',                 price: '$14.99/mes' },
  { key: 'og_pro_plan_id',        label: 'OG Pro',                    price: '$35.99/mes' },
  { key: 'og_unlimited_plan_id',  label: 'OG Unlimited',              price: '$99/mes' },
  { key: 'ultimate_pack_plan_id', label: 'OG MUSIC Ultimate Pack',    price: '$149.99/mes' },
];

export default function PaypalSubscriptionsPanel() {
  const [cfg, setCfg] = useState({ configured: false });
  const [busy, setBusy] = useState(false);
  const [savingPlans, setSavingPlans] = useState(false);
  const [planIds, setPlanIds] = useState({
    og_access_plan_id: '',
    og_pro_plan_id: '',
    og_unlimited_plan_id: '',
    ultimate_pack_plan_id: '',
  });

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/paypal-subscriptions/admin/config`, { withCredentials: true });
      setCfg(r.data || { configured: false });
      setPlanIds({
        og_access_plan_id: r.data?.og_access_plan_id || '',
        og_pro_plan_id: r.data?.og_pro_plan_id || '',
        og_unlimited_plan_id: r.data?.og_unlimited_plan_id || '',
        ultimate_pack_plan_id: r.data?.ultimate_pack_plan_id || '',
      });
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const handleAutoSetup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/paypal-subscriptions/admin/auto-setup`, {}, { withCredentials: true });
      if (r.data?.already_configured) {
        toast.info('Ya configurado');
      } else {
        toast.success(`Plan creado: ${r.data?.mensual_plan_id}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en setup');
    } finally { setBusy(false); }
  };

  const handleSavePlans = async () => {
    setSavingPlans(true);
    try {
      await axios.post(`${API_URL}/api/paypal-subscriptions/admin/config`, planIds, { withCredentials: true });
      toast.success('Plan IDs de membresías guardados');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSavingPlans(false); }
  };

  return (
    <div className="glass-card p-6" data-testid="settings-paypal-subs">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
          <RotateCcw className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h3 className="font-semibold">PayPal Subscriptions · Mensual OG Mastering</h3>
          <p className="text-xs text-white/40">Suscripción recurrente $11.99/mes para el plan Mensual Ilimitado</p>
        </div>
      </div>

      {cfg.configured ? (
        <div className="p-4 bg-green-500/5 border border-green-400/20 rounded-lg mb-4">
          <p className="text-sm font-medium text-green-400 mb-1">✓ Configurado</p>
          <p className="text-xs text-white/60">Plan ID: <code className="text-white/80">{cfg.mensual_plan_id}</code></p>
          {cfg.webhook_id && <p className="text-xs text-white/60 mt-1">Webhook ID: <code className="text-white/80">{cfg.webhook_id}</code></p>}
          <div className="mt-3 p-3 bg-black/40 border border-white/10 rounded">
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1 font-bold">Webhook URL oficial (producción)</p>
            <code className="text-xs text-[#E8720C] break-all select-all">{cfg.official_webhook_url || `${API_URL}/api/paypal-subscriptions/webhook`}</code>
            <p className="text-[10px] text-white/40 mt-2">Pega esta URL en PayPal Developer → Apps → Webhooks. Eventos: BILLING.SUBSCRIPTION.ACTIVATED, .RENEWED, .CANCELLED, .EXPIRED, .SUSPENDED</p>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-amber-500/5 border border-amber-400/20 rounded-lg mb-4">
          <p className="text-sm font-medium text-amber-400 mb-2">⚙ No configurado</p>
          <p className="text-xs text-white/60 leading-relaxed">Click el botón para crear automáticamente el Product + Plan en tu cuenta PayPal LIVE. Mientras no esté configurado, los clientes pagan con el flujo de pago único.</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleAutoSetup}
          disabled={busy || cfg.configured}
          className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="paypal-subs-setup-btn"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando…</> : (cfg.configured ? '✓ Listo' : '🚀 Auto-Setup en PayPal')}
        </button>
      </div>

      <p className="text-[10px] text-white/40 mt-4 leading-relaxed">
        💡 Para activar la facturación recurrente, registra el webhook URL en tu PayPal Developer Dashboard con los eventos: BILLING.SUBSCRIPTION.ACTIVATED, .RENEWED, .CANCELLED, .EXPIRED, .SUSPENDED.
      </p>

      {/* ─── Membresías recurrentes ─── */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-semibold text-sm">Membresías · Plan IDs PayPal</h4>
            <p className="text-[11px] text-white/40">Asocia cada tier de membresía con su Plan ID recurrente de PayPal.</p>
          </div>
          {cfg.memberships_configured && (
            <span className="text-[10px] uppercase tracking-wider text-green-400 font-bold">✓ Activas</span>
          )}
        </div>
        <div className="space-y-3" data-testid="membership-plan-ids-form">
          {TIERS.map(tier => (
            <div key={tier.key} className="flex items-center gap-3">
              <div className="min-w-[180px]">
                <p className="text-sm font-medium text-white">{tier.label}</p>
                <p className="text-[11px] text-white/40">{tier.price}</p>
              </div>
              <input
                value={planIds[tier.key]}
                onChange={(e) => setPlanIds(prev => ({ ...prev, [tier.key]: e.target.value }))}
                placeholder="P-XXXXXXXXXX"
                className="flex-1 input-glass font-mono text-xs"
                data-testid={`plan-id-input-${tier.key}`}
              />
            </div>
          ))}
        </div>
        <button
          onClick={handleSavePlans}
          disabled={savingPlans}
          className="mt-4 px-5 py-2.5 bg-[#0070BA] hover:bg-[#005A99] rounded-lg text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="save-membership-plans-btn"
        >
          {savingPlans ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</> : <><Save className="w-4 h-4" /> Guardar Plan IDs</>}
        </button>
      </div>
    </div>
  );
}
